<template>
  <EnhancedDataTable
    ref="dataTable"
    title="Products"
    :items="allProducts || []"
    :headers="headers"
    item-key="Product_SK"
    selection-state-name="selectedProductSks"
    :loading="allProductsLoading ? 'info' : false"
    :initial-items-per-page="20"
    :footer-props="{ itemsPerPageOptions: [-1, 10, 15, 20, 25, 50, 100] }"
    show-select
    select-strategy="page"
    show-crud-actions
    show-reset-filters
    show-unselect-all
    entity-name="Product"
    :showExpand="true"
    :expanded="expandedRows"
    @expanded="expandedRows = $event"
    @add="openDialog()"
    @edit="openDialog($event)"
    @delete="handleDelete($event)"
    @merge="openMergeDialog($event)"
    max-height="calc(100vh - 180px)"
  >
    <template #expanded-content="{ item }">
      <ProductMatchingsSubTable :productSk="item.Product_SK" />
    </template>
  </EnhancedDataTable>

  <MasterProductDialog
    :dialog="viewMasterProductDialog"
    :masterProduct="editedItem"
    @closeDialog="closeMasterProductDialog()"
  />

  <MergeMasterDataDialog
    v-model="mergeDialog"
    :selected-records="selectedRecordsForMerge"
    :entity-name="'Products'"
    item-key="Product_SK"
    display-field="Product_Name"
    :loading="mergeLoading"
    @merge="handleMerge"
  />

  <DeleteConfirmationDialog
    v-model="deleteDialog"
    entity-name="Product"
    :item-name="itemToDelete?.Product_Name"
    @confirm="confirmDelete"
  />
</template>

<script setup>
import { ref, computed, inject, watch } from 'vue'
import { useStore } from 'vuex'
import { useMutation } from '@vue/apollo-composable'

import EnhancedDataTable from '@/components/EnhancedDataTable.vue'
import MasterProductDialog from '@/components/MasterProductDialog.vue'
import MergeMasterDataDialog from '@/components/MergeMasterDataDialog.vue'
import DeleteConfirmationDialog from '@/components/DeleteConfirmationDialog.vue'
import ProductMatchingsSubTable from './ProductMatchingsSubTable.vue'

import REMOVE_PRODUCT from '@/graphql/Product/removeProduct.gql'
import MERGE_PRODUCT from '@/graphql/Product/mergeProduct.gql'

const store = useStore()

// Use App.vue's allProducts data via inject (respects selection filtering)
const rc = inject('rc')
const allProducts = computed(() => rc?.allProducts || [])
const allProductsLoading = computed(() => rc?.$apollo?.queries?.allProducts?.loading || false)

const headers = [
  { title: '#', key: 'Product_SK', filterType: 'number', align: 'center' },
  { title: 'Company', key: 'Company_Name', filterType: 'combo', align: 'left' },
  // { title: 'House', key: 'House_Name', filterType: 'combo', align: 'leftyou' },
  { title: 'Brand', key: 'Brand_Name', filterType: 'combo', align: 'left' },
  { title: 'Line', key: 'Line_Name', filterType: 'combo', align: 'left' },
  { title: 'Product', key: 'Product_Name', filterType: 'text', align: 'left' },
  { title: 'Category', key: 'Category_Name', filterType: 'combo', align: 'left' },
  { title: 'Benefit', key: 'Benefit_Name', filterType: 'list', align: 'left' },
  { title: 'Multi Pack', key: 'Multi_Pack_Name', filterType: 'list', align: 'center' },
  { title: 'TREX', key: 'TREX', filterType: 'list', align: 'center' },
  { title: 'Total $', key: 'Total_Amount_USD', align: 'end' },
  { title: 'Log', key: 'Mutation_Logs_Count', align: 'center', width: '60px' },
  { title: 'Actions', key: 'actions', sortable: false, align: 'center', width: '120px' }
]

const dataTable = ref(null)
const editedIndex = ref(-1)
const editedItem = ref({})
const viewMasterProductDialog = ref(false)
const expandedRows = ref([])

// Collapse all expansions when product matching mutations occur on other pages
// This ensures fresh data is fetched when user re-expands after navigating back
watch(
  () => store.state.productMatchingMutationVersion,
  () => {
    expandedRows.value = []
  }
)

const mergeDialog = ref(false)
const mergeLoading = ref(false)
const selectedRecordsForMerge = ref([])
const deleteDialog = ref(false)
const itemToDelete = ref(null)

const addError = (message) => store.commit('addError', message)
const addSuccess = (message) => store.commit('addSuccess', message)

const openDialog = (item = null) => {
  if (item) {
    editedIndex.value = allProducts.value?.indexOf(item) ?? -1
    editedItem.value = { ...item }
  } else {
    editedIndex.value = -1
    editedItem.value = {}
  }
  viewMasterProductDialog.value = true
}

const closeMasterProductDialog = () => {
  viewMasterProductDialog.value = false
  setTimeout(() => {
    editedItem.value = {}
    editedIndex.value = -1
  }, 300)
}

const { mutate: removeProduct } = useMutation(REMOVE_PRODUCT)

const handleDelete = (item) => {
  itemToDelete.value = item
  deleteDialog.value = true
}

const confirmDelete = () => {
  const item = itemToDelete.value
  if (!item) return

  removeProduct({
    Product_SK: item.Product_SK
  }, {
    update: (cache) => {
      cache.evict({ id: `Product:${item.Product_SK}` })
      cache.gc()
    }
  })
    .then(() => {
      // Remove deleted item from selection
      dataTable.value?.removeFromSelection(item.Product_SK)
    })
    .catch((error) => addError(error.message))
    .finally(() => {
      itemToDelete.value = null
    })
}

const openMergeDialog = (records) => {
  selectedRecordsForMerge.value = records
  mergeDialog.value = true
}

const { mutate: mergeProduct } = useMutation(MERGE_PRODUCT)

const handleMerge = (mergeData) => {
  mergeLoading.value = true
  mergeProduct({
    MergeProductInput: {
      survivor_Product_SK: mergeData.survivor,
      duplicate_Product_SKs: mergeData.duplicates
    }
  }, {
    update: (cache, { data: { mergeProduct } }) => {
      // Evict duplicates from cache
      mergeData.duplicates.forEach(id => {
        cache.evict({ id: `Product:${id}` })
      })
      cache.gc()
      // Increment survivor's mutation log count
      cache.modify({
        id: `Product:${mergeProduct.Product_SK}`,
        fields: {
          Mutation_Logs_Count(count) {
            return (count || 0) + 1
          }
        }
      })
    }
  })
    .then(() => {
      // Remove merged duplicates from selection (keep only survivor selected)
      dataTable.value?.removeFromSelection(mergeData.duplicates)
      mergeDialog.value = false
      addSuccess(`Successfully merged ${mergeData.duplicates.length + 1} products`)
    })
    .catch((error) => addError(error.message))
    .finally(() => {
      mergeLoading.value = false
    })
}
</script>
